import { isApiErrorBody } from '@gatecheckpro/contracts';

/**
 * The browser client every GateCheckPro web surface talks to the API through.
 *
 * Shared because it is *infrastructure*, not domain: typed refusals, session
 * rotation and the storage of an opaque credential are identical concerns for
 * an attendee buying a ticket and an operator reviewing an organizer. No domain
 * knowledge lives here, and none should — the moment this module starts knowing
 * what an event is, it has become the coupling the module boundaries exist to
 * prevent.
 */

/**
 * A refusal the server made deliberately.
 *
 * Callers branch on `code`, never on `message`. The message is for humans and
 * may be reworded at any time; the code is a contract.
 */
export class ApiRefusal extends Error {
  readonly code: string;
  readonly status: number;
  readonly reasonCode: string | undefined;
  readonly details: Record<string, unknown> | undefined;
  readonly requestId: string;

  constructor(init: {
    code: string;
    message: string;
    status: number;
    reasonCode?: string;
    details?: Record<string, unknown>;
    requestId: string;
  }) {
    super(init.message);
    this.name = 'ApiRefusal';
    this.code = init.code;
    this.status = init.status;
    this.reasonCode = init.reasonCode;
    this.details = init.details;
    this.requestId = init.requestId;
  }
}

/**
 * The network did not reach the API at all.
 *
 * Kept distinct from a refusal on purpose. "We could not reach GateCheckPro" and
 * "GateCheckPro said no" are different facts, and a surface that shows the same
 * message for both teaches users to distrust it (SDAD Section 49).
 */
export class ApiUnreachable extends Error {
  constructor(cause: unknown) {
    super('Could not reach GateCheckPro.');
    this.name = 'ApiUnreachable';
    this.cause = cause;
  }
}

export interface StoredSession {
  readonly accessToken: string;
  readonly accessTokenExpiresAt: string;
  readonly sessionToken: string;
  readonly sessionExpiresAt: string;
  readonly mfaSatisfied: boolean;
}

export interface SessionStore {
  read(): StoredSession | null;
  write(session: StoredSession | null): void;
}

/**
 * Session storage.
 *
 * `localStorage`, with every access guarded: a private window, cleared site data
 * or a browser configured to block storage all make the accessor throw, and a
 * surface that crashes on load because storage was unavailable is worse than one
 * that simply asks the user to sign in again.
 */
export function browserSessionStore(key: string): SessionStore {
  return {
    read() {
      try {
        const raw = window.localStorage.getItem(key);
        if (raw === null) return null;
        return JSON.parse(raw) as StoredSession;
      } catch {
        return null;
      }
    },
    write(session) {
      try {
        if (session === null) window.localStorage.removeItem(key);
        else window.localStorage.setItem(key, JSON.stringify(session));
      } catch {
        // Storage is unavailable. The session still works for this page load.
      }
    },
  };
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  /** Sent as `Idempotency-Key`, for commands that must not double-apply. */
  readonly idempotencyKey?: string;
  /**
   * Sent as `If-Match`, for writes that must not land on top of a version the
   * caller has never seen.
   *
   * Infrastructure rather than domain, exactly like the idempotency key beside
   * it: a conditional write is an HTTP mechanism, and the alternative is every
   * surface that needs one reaching past this client to `fetch` and losing
   * session rotation and typed refusals on the way.
   */
  readonly ifMatch?: string;
  readonly signal?: AbortSignal;
  /** Skips the access token. Used by sign-in and registration. */
  readonly anonymous?: boolean;
}

export interface ApiClient {
  request<T>(path: string, options?: RequestOptions): Promise<T>;
  session(): StoredSession | null;
  setSession(session: StoredSession | null): void;
  onSessionChange(listener: (session: StoredSession | null) => void): () => void;
}

export function createApiClient(options: { baseUrl: string; store: SessionStore }): ApiClient {
  const listeners = new Set<(session: StoredSession | null) => void>();
  let current = options.store.read();
  /** Serialises refresh so a burst of 401s produces one rotation, not several. */
  let inFlightRefresh: Promise<StoredSession | null> | null = null;

  function setSession(session: StoredSession | null): void {
    current = session;
    options.store.write(session);
    for (const listener of listeners) listener(session);
  }

  async function send(path: string, opts: RequestOptions, token: string | null): Promise<Response> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (opts.body !== undefined) headers['content-type'] = 'application/json';
    if (token !== null) headers.authorization = `Bearer ${token}`;
    if (opts.idempotencyKey !== undefined) headers['idempotency-key'] = opts.idempotencyKey;
    if (opts.ifMatch !== undefined) headers['if-match'] = `"${opts.ifMatch}"`;

    try {
      return await fetch(`${options.baseUrl}${path}`, {
        method: opts.method ?? 'GET',
        headers,
        body: opts.body === undefined ? null : JSON.stringify(opts.body),
        signal: opts.signal ?? null,
      });
    } catch (error) {
      throw new ApiUnreachable(error);
    }
  }

  /**
   * Exchanges the session credential for a fresh access token.
   *
   * The session credential rotates on every refresh, so the new one must be
   * stored before anything else uses it. A failed refresh clears the session:
   * the credential is spent either way, and pretending otherwise leaves the
   * surface in a state where every subsequent request fails silently.
   */
  async function refresh(): Promise<StoredSession | null> {
    if (inFlightRefresh !== null) return inFlightRefresh;
    const existing = current;
    if (existing === null) return null;

    inFlightRefresh = (async () => {
      const response = await send(
        '/api/v1/auth/refresh',
        { method: 'POST', body: { sessionToken: existing.sessionToken }, anonymous: true },
        null,
      );
      if (!response.ok) {
        setSession(null);
        return null;
      }
      const next = (await response.json()) as StoredSession;
      setSession(next);
      return next;
    })().finally(() => {
      inFlightRefresh = null;
    });

    return inFlightRefresh;
  }

  async function toRefusal(response: Response): Promise<ApiRefusal> {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      // A response with no JSON body is still a refusal; it just carries less.
    }
    if (isApiErrorBody(body)) {
      return new ApiRefusal({
        code: body.error.code,
        message: body.error.message,
        status: response.status,
        ...(body.error.reasonCode === undefined ? {} : { reasonCode: body.error.reasonCode }),
        ...(body.error.details === undefined ? {} : { details: body.error.details }),
        requestId: body.error.requestId,
      });
    }
    return new ApiRefusal({
      code: 'INTERNAL_ERROR',
      message: 'GateCheckPro returned an unexpected response.',
      status: response.status,
      requestId: '',
    });
  }

  return {
    session: () => current,
    setSession,

    onSessionChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    async request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
      const anonymous = opts.anonymous === true;
      let response = await send(path, opts, anonymous ? null : (current?.accessToken ?? null));

      // One retry after a rotation. A second 401 means the session is genuinely
      // gone, and retrying again would only loop.
      if (response.status === 401 && !anonymous && current !== null) {
        const refreshed = await refresh();
        if (refreshed !== null) {
          response = await send(path, opts, refreshed.accessToken);
        }
      }

      if (!response.ok) throw await toRefusal(response);
      if (response.status === 204) return undefined as T;
      return (await response.json()) as T;
    },
  };
}
